import actionCreators from 'actionCreators';
import { useActions } from 'components/shared/hooks/useActions';
import { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { USER_LOADED_STATUS } from '../constants';

/**
 * @description Triggers loadSingleUser action if user not present in store
 * @param {string} userIdOrNumber
 * @param {boolean} shouldRefetch - Whether the user should be refetched on mount; default false
 * @param {number} minimumLoadingStatus - What is the minimum load level required for this user; default USER_LOADED_STATUS.SEARCH
 * @returns {object} { loading: boolean, user: object | undefined }
 */
export const useSingleUser = (
  userIdOrNumber,
  shouldRefetch = false,
  minimumLoadingStatus = USER_LOADED_STATUS.FULL
) => {
  const { usersById, loadingAll } = useSelector(state => state.toJS().users);
  const loadSingleUser = useActions(actionCreators.userActions.loadSingleUser);
  const getEstheticianDisplayName = useActions(
    actionCreators.userActions.getEstheticianDisplayName
  );

  const userNumberIdMap = useMemo(
    () =>
      Object.values(usersById).reduce((acc, user) => {
        if (!user?.userNumber) return acc;

        acc[user.userNumber] = user.id;
        return acc;
      }, {}),
    [usersById]
  );

  const userIdToUse = useMemo(() => {
    if (!userIdOrNumber) return undefined;
    return userNumberIdMap[userIdOrNumber] ?? userIdOrNumber;
  }, [userIdOrNumber, userNumberIdMap]);

  useEffect(() => {
    const shouldRefreshUser = () => {
      // No userId, no fetch
      if (!userIdOrNumber) return false;
      // A bulk load is currently in progress, no fetch
      if (loadingAll) return false;
      // A single user load is in progress, no fetch
      if (usersById?.[userIdOrNumber]?.loading) return false;
      // If shouldRefetch is true, always fetch
      if (shouldRefetch) return true;
      // If user is not in store, fetch
      if (!usersById[userIdToUse]) return true;
      // If we have basic information, but need more, fetch
      if ((usersById[userIdToUse]?.loadingStatus ?? 3) < minimumLoadingStatus)
        return true;
      // If a more complete request was rejected and we only need the basic info load again.
      if (
        usersById[userIdOrNumber].accessDenied === true &&
        minimumLoadingStatus === USER_LOADED_STATUS.BASIC
      )
        return true;
      return false;
    };

    if (!shouldRefreshUser()) return;

    if (minimumLoadingStatus === USER_LOADED_STATUS.BASIC) {
      getEstheticianDisplayName(userIdOrNumber);
      return;
    }

    loadSingleUser(userIdOrNumber);
  }, [userIdOrNumber, user, shouldRefetch, minimumLoadingStatus]);

  const loading = useMemo(() => {
    if (!userIdOrNumber) return false;
    return usersById[userIdOrNumber]?.loading ?? false;
  }, [userIdOrNumber, usersById]);

  const user = useMemo(() => {
    if (!userIdToUse) return undefined;
    return usersById[userIdToUse];
  }, [usersById, userIdToUse]);

  const refetch = useCallback(async () => {
    if (!userIdOrNumber) return;
    if (minimumLoadingStatus === USER_LOADED_STATUS.BASIC)
      await getEstheticianDisplayName(userIdOrNumber);
    else await loadSingleUser(userIdOrNumber);
  }, [userIdOrNumber]);

  return {
    user,
    userId: userIdToUse,
    loading,
    refetch,
  };
};
